import React from "react";
import { MdLocationOn } from "react-icons/md";
import { STATES_LIST } from "Constants/constant";
import useCareLabels from "hooks/useCareLabels";

/**
 * ShiftLocationSelector Component
 * A reusable component for displaying and selecting shift locations
 *
 * @param {Object} props
 * @param {Object} props.locationData - The location data object (from shift_location)
 * @param {Function} props.onSelectLocation - Callback when location button is clicked
 * @param {Function} props.onClearLocation - Callback when clear button is clicked
 * @param {Function} props.onUseParticipantLocation - Callback to revert to participant's location (optional)
 * @param {boolean} props.isManuallySet - Whether the location was manually set (shows "Use Participant Location" button)
 * @param {boolean} props.hasParticipant - Whether a participant is selected (enables "Use Participant Location" button)
 * @param {boolean} props.disabled - Whether the component is disabled
 * @param {string} props.buttonLabel - Custom label for the button (default: "Select Shift Location")
 * @param {string} props.className - Additional CSS classes
 */
const ShiftLocationSelector = ({
  locationData,
  onSelectLocation,
  onClearLocation,
  onUseParticipantLocation,
  isManuallySet = false,
  hasParticipant = false,
  disabled = false,
  buttonLabel = "Select Shift Location",
  className = "",
}) => {
  const { participantLabel } = useCareLabels();

  // Parse location data (might be JSON string or object)
  const getLocationData = () => {
    if (!locationData) return null;

    if (typeof locationData === "string") {
      try {
        return JSON.parse(locationData);
      } catch (e) {
        return null;
      }
    }
    return locationData;
  };

  const parsedLocationData = getLocationData();

  const hasLocation =
    parsedLocationData &&
    (parsedLocationData.street ||
      parsedLocationData.street_address ||
      parsedLocationData.suburb ||
      parsedLocationData.postcode);

  // Convert text to title case (capitalize first letter of each word)
  const toTitleCase = (text) => {
    if (text == null || text === "") return "";
    const str = String(text).trim();
    if (!str) return "";

    const abbreviations = {
      st: "St",
      street: "Street",
      rd: "Rd",
      road: "Road",
      ave: "Ave",
      avenue: "Avenue",
      dr: "Dr",
      drive: "Drive",
      ct: "Ct",
      court: "Court",
      pl: "Pl",
      place: "Place",
      ln: "Ln",
      lane: "Lane",
      blvd: "Blvd",
      boulevard: "Boulevard",
      cres: "Cres",
      crescent: "Crescent",
      pde: "Pde",
      parade: "Parade",
      ter: "Ter",
      terrace: "Terrace",
      way: "Way",
      hwy: "Hwy",
      highway: "Highway",
    };

    return str
      .toLowerCase()
      .split(/\s+/)
      .map((word) => {
        const lowerWord = word.toLowerCase();
        if (abbreviations[lowerWord]) {
          return abbreviations[lowerWord];
        }
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(" ");
  };

  const getStateAbbreviation = (stateName) => {
    if (stateName == null || stateName === "") return "";
    const normalized = String(stateName).trim();
    if (!normalized) return "";

    const lower = normalized.toLowerCase();

    const stateOption = STATES_LIST.find(
      (s) => s.value?.toLowerCase() === lower
    );
    if (stateOption) {
      return stateOption.label;
    }

    const stateByLabel = STATES_LIST.find(
      (s) => s.label?.toLowerCase() === lower
    );
    if (stateByLabel) {
      return stateByLabel.label;
    }

    return toTitleCase(normalized);
  };

  const getLocationDisplayText = () => {
    if (!hasLocation) {
      return buttonLabel;
    }
    const parts = [];

    if (parsedLocationData.street || parsedLocationData.street_address) {
      const street =
        parsedLocationData.street || parsedLocationData.street_address;
      parts.push(toTitleCase(street));
    }

    if (parsedLocationData.suburb) {
      parts.push(toTitleCase(parsedLocationData.suburb));
    }

    if (parsedLocationData.state_name || parsedLocationData.state) {
      const state =
        parsedLocationData.state_name || parsedLocationData.state;
      parts.push(getStateAbbreviation(state));
    }

    if (parsedLocationData.postcode) {
      parts.push(parsedLocationData.postcode);
    }

    return parts.length > 0 ? parts.join(", ") : buttonLabel;
  };

  const locationText = getLocationDisplayText();

  const handleClearLocation = (e) => {
    e.stopPropagation();
    if (onClearLocation) {
      onClearLocation();
    }
  };

  const handleUseParticipantLocation = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onUseParticipantLocation) {
      onUseParticipantLocation();
    }
  };

  return (
    <div
      className={`flex items-center gap-1.5 min-w-0 ${className}`}
      style={{ position: "relative" }}
    >
      <button
        type="button"
        onClick={onSelectLocation}
        disabled={disabled}
        className={`
          flex items-center gap-1.5 px-3 py-1.5 rounded-md border transition-all
          ${
            hasLocation
              ? "bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
              : "bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
          }
          ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
          min-w-0
        `}
        title={hasLocation ? locationText : buttonLabel}
      >
        <MdLocationOn
          size={18}
          className={
            hasLocation
              ? "text-green-600 flex-shrink-0"
              : "text-blue-600 flex-shrink-0"
          }
        />
        <span className="text-sm font-medium whitespace-nowrap truncate">
          {locationText}
        </span>
      </button>

      {hasLocation &&
        isManuallySet &&
        hasParticipant &&
        onUseParticipantLocation &&
        !disabled && (
          <button
            type="button"
            onClick={handleUseParticipantLocation}
            onMouseDown={(e) => e.stopPropagation()}
            className="flex-shrink-0 px-2 py-1.5 text-xs font-medium rounded-md hover:bg-blue-50 text-blue-600 hover:text-blue-700 transition-colors border border-blue-200 hover:border-blue-300 cursor-pointer relative z-10 active:bg-blue-100"
            title={`Use ${participantLabel.toLowerCase()}'s location`}
            aria-label={`Use ${participantLabel.toLowerCase()}'s location`}
            style={{ pointerEvents: "auto", zIndex: 10 }}
          >
            Use {participantLabel} Location
          </button>
        )}

      {hasLocation && onClearLocation && !disabled && (
        <button
          type="button"
          onClick={handleClearLocation}
          className="flex-shrink-0 p-1.5 rounded-md hover:bg-red-50 text-red-500 hover:text-red-700 transition-colors border border-transparent hover:border-red-200"
          title="Clear shift location"
          aria-label="Clear shift location"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      )}
    </div>
  );
};

export default ShiftLocationSelector;
